/* Room chat — real Matrix m.room.message timelines. Live via sync, backfilled
   via /messages, optimistic send reconciled by transaction_id. No mock messages. */
import { defineStore } from 'pinia'
import { matrix } from '@/lib/matrix'
import { sync } from '@/lib/sync'
import { useFloor } from '@/stores/floor'
import { useNotify } from '@/stores/notify'
import { extractMentions } from '@/lib/mentions'
import type { ChatMessage, MeetingReadout, MessageReaction } from '@/types/atrium'
import type { MatrixEvent, RoomMessageContent } from '@/types/matrix'

function msgFrom(ev: MatrixEvent): ChatMessage {
  const c = ev.content as RoomMessageContent
  const rel = c?.['m.relates_to'] as { 'm.in_reply_to'?: { event_id?: string } } | undefined
  const replyTo = rel?.['m.in_reply_to']?.event_id
  const m: ChatMessage = {
    id: ev.event_id || 'ev-' + (ev.origin_server_ts || Date.now()),
    sender: ev.sender || '',
    body: c?.body || '',
    ts: ev.origin_server_ts || Date.now(),
  }
  if (replyTo) m.replyTo = replyTo
  // Media msgtypes (ours and other Matrix clients') carry the attachment in
  // url/info — surface it so the timeline renders a real attachment, not the
  // filename as text.
  const MEDIA_TYPES = ['m.file', 'm.image', 'm.video', 'm.audio']
  if (MEDIA_TYPES.includes(String(c?.msgtype)) && typeof c?.url === 'string') {
    const info = (c.info ?? {}) as { mimetype?: string; size?: number }
    m.kind = 'file'
    m.file = {
      url: c.url as string,
      name: (typeof c.filename === 'string' && c.filename) || c.body || 'file',
      mime: info.mimetype,
      size: info.size,
    }
  }
  return m
}

/** Per-key aggregation of m.reaction annotations on one message. */
interface ReactionAgg {
  count: number
  senders: string[]
  mine: string | null // my reaction's event id (redact target)
  url?: string        // mxc image for custom-emote keys
}

/** A meeting summary (app.atrium.readout, §11) as a timeline entry — rendered
    as a card between the ordinary bubbles. */
function readoutFrom(ev: MatrixEvent): ChatMessage | null {
  const c = ev.content as unknown as MeetingReadout
  if (!c || c.kind !== 'meeting_summary' || !c.summary_md) return null
  return {
    id: ev.event_id || 'ro-' + (ev.origin_server_ts || Date.now()),
    sender: ev.sender || '',
    body: '',
    ts: ev.origin_server_ts || Date.now(),
    kind: 'summary',
    summary: c,
  }
}

// The room timeline is dominated by app.atrium.room.heat + m.rtc.member events
// (~166 of those per actual message). Asking the server for messages only lets
// it page through that noise so backfill returns real chat, not machine events.
// Meeting readouts ride along — they belong in the conversation.
const MESSAGE_FILTER = { types: ['m.room.message', 'app.atrium.readout', 'm.reaction', 'm.room.redaction'] }
const PAGE_LIMIT = 100 // events per /messages call (post-filter, so up to 100 messages)
const WANT = 30        // target new messages to gather per backfill / load-older

interface ChatState {
  byRoom: Record<string, ChatMessage[]>
  backfilled: Record<string, boolean>
  pageToken: Record<string, string | undefined> // /messages `end` cursor — where to keep paging back
  atStart: Record<string, boolean>               // reached the start of readable history
  loadingOlder: Record<string, boolean>
  inited: boolean
  // Reactions: room → target event → key → aggregate. Kept apart from the
  // message array so a chip toggle never reorders / re-renders the timeline.
  reactions: Record<string, Record<string, Record<string, ReactionAgg>>>
  // reaction event id → where it counted, so a redaction can undo it.
  reactionIndex: Record<string, { roomId: string; target: string; key: string; sender: string }>
  // Redactions seen before their reaction (backfill walks history backwards).
  redactedIds: Record<string, true>
  // Reply targets that fell out of the local timeline, fetched on demand.
  replyCache: Record<string, { sender: string; body: string } | null>
}

export const useChat = defineStore('chat', {
  state: (): ChatState => ({
    byRoom: {}, backfilled: {}, pageToken: {}, atStart: {}, loadingOlder: {}, inited: false,
    reactions: {}, reactionIndex: {}, redactedIds: {}, replyCache: {},
  }),

  getters: {
    messages: (s) => (roomId: string): ChatMessage[] => s.byRoom[roomId] || [],
    // True once the first backfill ran and there is still older history to fetch.
    hasOlder: (s) => (roomId: string): boolean => s.backfilled[roomId] === true && !s.atStart[roomId],
    olderLoading: (s) => (roomId: string): boolean => !!s.loadingOlder[roomId],
    /** Reaction chips for one message, mine-first stable order. */
    reactionsOf: (s) => (roomId: string, eventId: string): MessageReaction[] => {
      const byKey = s.reactions[roomId]?.[eventId]
      if (!byKey) return []
      return Object.entries(byKey)
        .filter(([, a]) => a.count > 0)
        .map(([key, a]) => ({ key, count: a.count, mine: a.mine, senders: a.senders, url: a.url }))
    },
    /** The message a reply points at — from the loaded timeline, else the fetch cache. */
    replyTarget: (s) => (roomId: string, eventId: string): { sender: string; body: string } | null => {
      const local = (s.byRoom[roomId] || []).find((m) => m.id === eventId)
      if (local) return { sender: local.sender, body: local.body }
      return s.replyCache[eventId] ?? null
    },
  },

  actions: {
    init() {
      if (this.inited) return
      this.inited = true
      sync.on('message', (roomId, ev) => this.ingest(roomId, ev))
      sync.on('reaction', (roomId, ev) => this.onReactionEvent(roomId, ev))
      // Live meeting readouts arrive on the custom channel (app.atrium.*
      // timeline events aren't m.room.message).
      sync.on('custom', (roomId, ev) => {
        if (ev.type === 'app.atrium.readout') this.ingest(roomId, ev)
      })
    },

    arr(roomId: string): ChatMessage[] {
      if (!this.byRoom[roomId]) this.byRoom[roomId] = []
      return this.byRoom[roomId]
    },

    ingest(roomId: string, ev: MatrixEvent) {
      if (!ev) return
      if (ev.type === 'app.atrium.readout') {
        const arr = this.arr(roomId)
        const m = readoutFrom(ev)
        if (!m || arr.some((x) => x.id === m.id)) return
        arr.push(m)
        arr.sort((a, b) => a.ts - b.ts)
        return
      }
      if (ev.type !== 'm.room.message') return
      const arr = this.arr(roomId)
      const txn = ev.unsigned?.transaction_id
      if (txn) {
        const i = arr.findIndex((m) => m.pending && m.txn === txn)
        if (i >= 0) { arr[i] = msgFrom(ev); return } // our own echo → confirm
      }
      if (arr.some((m) => m.id === ev.event_id)) return // dedupe
      arr.push(msgFrom(ev))
      arr.sort((a, b) => a.ts - b.ts)
      // Unread / @mention tracking off the same (deduped, non-echo) new event.
      useNotify().onMessage(roomId, ev)
    },

    /** Fold one m.reaction / m.room.redaction into the aggregates. Idempotent
     *  (dedup by event id) and order-tolerant: backfill pages walk history
     *  backwards, so a redaction can land before the reaction it undoes. */
    onReactionEvent(roomId: string, ev: MatrixEvent) {
      if (ev.type === 'm.reaction') {
        const id = ev.event_id
        const rel = (ev.content as Record<string, any>)?.['m.relates_to']
        if (!id || !ev.sender || rel?.rel_type !== 'm.annotation' || !rel.event_id || typeof rel.key !== 'string') return
        if (this.reactionIndex[id]) return // already counted (echo / re-page)
        if (this.redactedIds[id]) { delete this.redactedIds[id]; return } // undone before we saw it
        const key = String(rel.key).slice(0, 256)
        const room = this.reactions[roomId] || (this.reactions[roomId] = {})
        const byKey = room[rel.event_id] || (room[rel.event_id] = {})
        const agg = byKey[key] || (byKey[key] = { count: 0, senders: [], mine: null })
        agg.count++
        agg.senders.push(ev.sender)
        if (ev.sender === matrix.userId()) agg.mine = id
        const emoteUrl = (ev.content as Record<string, unknown>)['app.atrium.emote_url']
        if (typeof emoteUrl === 'string' && emoteUrl.startsWith('mxc://') && !agg.url) agg.url = emoteUrl
        this.reactionIndex[id] = { roomId, target: rel.event_id, key, sender: ev.sender }
        return
      }
      // m.room.redaction — `redacts` is top-level pre-v11, in content from v11.
      const redacts = (ev as Record<string, any>).redacts || (ev.content as Record<string, any>)?.redacts
      if (typeof redacts !== 'string') return
      const hit = this.reactionIndex[redacts]
      if (!hit) {
        this.redactedIds[redacts] = true // maybe a reaction we haven't paged to yet
        // A redacted message: pull it from the timeline (it's gone server-side too).
        const arr = this.byRoom[roomId]
        if (arr) {
          const i = arr.findIndex((m) => m.id === redacts)
          if (i >= 0) arr.splice(i, 1)
        }
        return
      }
      delete this.reactionIndex[redacts]
      const agg = this.reactions[hit.roomId]?.[hit.target]?.[hit.key]
      if (!agg) return
      agg.count = Math.max(0, agg.count - 1)
      agg.senders = agg.senders.filter((s2) => s2 !== hit.sender)
      if (agg.mine === redacts) agg.mine = null
      if (agg.count === 0) delete this.reactions[hit.roomId][hit.target][hit.key]
    },

    /** Add my reaction, or take it back if the chip already carries it. */
    async toggleReaction(roomId: string, targetId: string, key: string, emoteUrl?: string) {
      const agg = this.reactions[roomId]?.[targetId]?.[key]
      try {
        if (agg?.mine) {
          await matrix.redactEvent(roomId, agg.mine)
          // Apply locally right away; the sync echo is a no-op (id already gone).
          this.onReactionEvent(roomId, { type: 'm.room.redaction', content: { redacts: agg.mine } } as unknown as MatrixEvent)
        } else {
          const res = await matrix.sendReaction(roomId, targetId, key, emoteUrl)
          this.onReactionEvent(roomId, {
            type: 'm.reaction',
            event_id: res.event_id,
            sender: matrix.userId(),
            content: {
              'm.relates_to': { rel_type: 'm.annotation', event_id: targetId, key },
              ...(emoteUrl ? { 'app.atrium.emote_url': emoteUrl } : {}),
            },
          } as unknown as MatrixEvent)
        }
      } catch (e) {
        console.warn('[chat] reaction failed', e)
      }
    },

    /** Make a reply's quoted message renderable even when it left the loaded
     *  timeline — fetch it once and cache (null = unreadable/gone). */
    async resolveReply(roomId: string, eventId: string) {
      if (eventId in this.replyCache) return
      if ((this.byRoom[roomId] || []).some((m) => m.id === eventId)) return
      this.replyCache[eventId] = null
      const ev = await matrix.getEvent(roomId, eventId)
      if (ev?.type === 'm.room.message') {
        const c = ev.content as RoomMessageContent
        this.replyCache[eventId] = { sender: ev.sender || '', body: c?.body || '' }
      }
    },

    async backfill(roomId: string) {
      if (this.backfilled[roomId]) return
      // Team rooms keep their history private: only members who can walk straight
      // in see past messages. Guests let in via knock see live chat from now on,
      // not the scrollback. Non-team rooms backfill for everyone.
      const floor = useFloor()
      const slug = floor.slugOf(roomId)
      const room = slug ? floor.roomBySlug(slug) : undefined
      if (room?.team && !floor.canWalkIn(room)) {
        this.backfilled[roomId] = true
        this.atStart[roomId] = true
        return
      }
      this.backfilled[roomId] = true
      await this.pageBack(roomId)
    },

    /** Fetch the next batch of older messages (driven by scroll-to-top). No-op
     *  once we've reached the start of readable history or while one is in flight. */
    async loadOlder(roomId: string) {
      if (!this.backfilled[roomId] || this.atStart[roomId] || this.loadingOlder[roomId]) return
      await this.pageBack(roomId)
    },

    /* Page /messages backward, asking the server for messages only, until we've
       gathered ~WANT new ones or hit the start. A single unfiltered 30-event grab
       (the old behaviour) landed almost entirely on heat/RTC events, which is why
       history looked empty on every cold load even though it was safe on the server. */
    async pageBack(roomId: string) {
      if (this.loadingOlder[roomId]) return
      this.loadingOlder[roomId] = true
      const arr = this.arr(roomId)
      const startCount = arr.length
      try {
        let from = this.pageToken[roomId]
        let pages = 0
        while (arr.length - startCount < WANT && !this.atStart[roomId] && pages++ < 10) {
          const data = await matrix.messages(roomId, { dir: 'b', limit: PAGE_LIMIT, from, filter: MESSAGE_FILTER })
          for (const ev of data.chunk || []) {
            if (ev.type === 'm.reaction' || ev.type === 'm.room.redaction') {
              this.onReactionEvent(roomId, ev)
              continue
            }
            if (arr.some((m) => m.id === ev.event_id)) continue
            if (ev.type === 'm.room.message') arr.push(msgFrom(ev))
            else if (ev.type === 'app.atrium.readout') {
              const m = readoutFrom(ev)
              if (m) arr.push(m)
            }
          }
          // No `end`, an unmoving cursor, or an empty page ⇒ start of history.
          if (!data.end || data.end === from || !data.chunk?.length) this.atStart[roomId] = true
          from = data.end
          this.pageToken[roomId] = from
        }
        arr.sort((a, b) => a.ts - b.ts)
      } catch {
        // Gained nothing on the very first attempt → let a later open retry.
        if (arr.length === startCount && !this.pageToken[roomId]) this.backfilled[roomId] = false
      } finally {
        this.loadingOlder[roomId] = false
      }
    },

    async send(roomId: string, text: string, replyTo?: string) {
      const t = (text || '').trim()
      if (!t) return
      const arr = this.arr(roomId)
      const tmp: ChatMessage = {
        id: 'pending-' + Date.now(), sender: matrix.userId() || '', body: t,
        ts: Date.now(), pending: true, txn: null,
      }
      if (replyTo) tmp.replyTo = replyTo
      arr.push(tmp)
      try {
        // Tell the homeserver who was mentioned (MSC3952) so detection is exact,
        // not a name-regex guess. The @[Name](mxid) tokens stay in the body and
        // render as pills.
        const userIds = extractMentions(t)
        const extra: Partial<RoomMessageContent> = {}
        if (userIds.length) extra['m.mentions'] = { user_ids: userIds }
        if (replyTo) extra['m.relates_to'] = { 'm.in_reply_to': { event_id: replyTo } }
        const res = await matrix.sendMessage(roomId, t, Object.keys(extra).length ? extra : undefined)
        tmp.txn = res.txn
      } catch (e) {
        tmp.failed = true
        console.warn('[chat] send failed', e)
      }
    },

    /** Send an uploaded file as its own m.file event (the Matrix-native shape,
        so other clients render it too). Images go through send() as markdown
        embeds instead — this is the everything-else path. */
    async sendFile(roomId: string, file: { mxc: string; name: string; mime?: string; size?: number }, replyTo?: string) {
      const arr = this.arr(roomId)
      const tmp: ChatMessage = {
        id: 'pending-' + Date.now(), sender: matrix.userId() || '', body: file.name,
        ts: Date.now(), pending: true, txn: null,
        kind: 'file', file: { url: file.mxc, name: file.name, mime: file.mime, size: file.size },
      }
      if (replyTo) tmp.replyTo = replyTo
      arr.push(tmp)
      try {
        const extra: Partial<RoomMessageContent> = {
          msgtype: 'm.file',
          filename: file.name,
          url: file.mxc,
          info: { ...(file.mime ? { mimetype: file.mime } : {}), ...(file.size ? { size: file.size } : {}) },
        }
        if (replyTo) extra['m.relates_to'] = { 'm.in_reply_to': { event_id: replyTo } }
        const res = await matrix.sendMessage(roomId, file.name, extra)
        tmp.txn = res.txn
      } catch (e) {
        tmp.failed = true
        console.warn('[chat] send file failed', e)
      }
    },
  },
})
